/**
 * Copyright 2023 Google LLC.
 * Copyright (c) Microsoft Corporation.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {assert} from '../../../utils/assert.js';
import type {BrowsingContextImpl} from '../context/BrowsingContextImpl.js';

import {InputState} from './InputState.js';

// We use a weak map here as specified here:
// https://www.w3.org/TR/webdriver/#dfn-browsing-context-input-state-map
export class InputStateManager extends WeakMap<
  BrowsingContextImpl,
  InputState
> {
  override get(context: BrowsingContextImpl): InputState {
    assert(context.isTopLevelContext());

    if (!this.has(context)) {
      this.set(context, new InputState());
    }

    return super.get(context)!;
  }
}
